import { enableProdMode, APP_INITIALIZER, ErrorHandler, importProvidersFrom } from '@angular/core';

import { environment } from './environments/environment';

import { AppComponent } from './app/app.component';
import { NgIdleModule } from '@ng-idle/core';
import { OnlineStatusModule } from 'ngx-online-status';
import { provideAnimations } from '@angular/platform-browser/animations';
import { withInterceptorsFromDi, provideHttpClient } from '@angular/common/http';
import { provideFirebaseApp } from '@angular/fire/app';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CommonModule } from '@angular/common';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { GlobalErrorHandler } from './app/services/global-error-handler.service';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule } from '@angular/material-moment-adapter';
import { MomentUtcDateAdapter, MY_FORMATS } from './app/adapters/date.adapter';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS, NativeDateModule } from '@angular/material/core';
import { SensorFormService } from './app/services/sensor-form.service';
import { ResponsiveService } from './app/services/responsive.service';
import { UpdateWorkerService } from './app/services/update-worker.service';
import { TranslateService } from './app/services/translate.service';
import { StorageManagerService } from './app/services/storage-manager.service';
import { StorageService } from './app/services/storage.service';
import { ThousandsPipe } from './app/shared/pipes/thousands.pipe';
import { LocaleDatePipe } from './app/shared/pipes/locale-date.pipe';
import { TransPipe } from './app/shared/pipes/trans.pipe';
// import { AuthGuardService } from './app/services/auth-guard.service';
import { Routes, provideRouter } from '@angular/router';
import { Overlay } from '@angular/cdk/overlay';
import { MAT_MENU_SCROLL_STRATEGY } from '@angular/material/menu';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY } from '@angular/material/autocomplete';
import { MAT_TOOLTIP_SCROLL_STRATEGY } from '@angular/material/tooltip';
import { MAT_DATEPICKER_SCROLL_STRATEGY } from '@angular/material/datepicker';
import { MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';
import { initSentry } from './sentry.config';
import { initFirebasePerformance, startTrace, stopTrace } from './app/core/monitoring/firebase-performance';
import { initWebVitalsFirebase } from './app/core/monitoring/web-vitals.firebase';
import { requestIdleOrTimeout, requestIdleOrTimeoutAsync } from './app/utils/idle-helper';

export function scrollFactory(overlay: Overlay): () => any {
  return () => overlay.scrollStrategies.reposition();
}

/**
 * Non-blocking translation setup for faster app startup
 */
function setupTranslateFactory(service: TranslateService): Function {
    return () => {
        const lang = localStorage.getItem('lang') || service.getBrowserLang() || 'es';
        // Non-blocking: Start loading but don't wait
        service.use(lang).catch(() => {
            // Fallback silencioso a español
            service.use('es').catch(console.warn);
        });
        
        // Return immediately resolved promise
        return Promise.resolve();
    };
}

/**
 * Completely non-blocking storage initialization
 */
function initializeStorageFactory(storageManager: StorageManagerService): Function {
  return () => {
    // Fire and forget - no waiting, start after app bootstrap
    setTimeout(() => {
      storageManager.cleanupExpiredData().catch(error => {
        console.warn('Storage cleanup failed:', error);
      });
    }, 0);
    
    // Return resolved promise immediately to avoid blocking
    return Promise.resolve();
  };
}

const routes: Routes = [
    { 
      path: '', 
      loadComponent: () => import('./app/pages/survey/survey.component').then(m => m.SurveyComponent),
      pathMatch: 'full' 
    },
    { 
      path: 'w/:uuid_survey/:api_domain',
      loadChildren: () => import('./app/pages/widget/widget.module').then(m => m.WidgetModule),
      pathMatch: 'full' 
    },
    { 
      path: 's/:uuid_survey/:api_domain',
      loadComponent: () => import('./app/pages/survey/survey.component').then(m => m.SurveyComponent),
      pathMatch: 'full' 
    },
    { 
      path: 's/:uuid_survey/:uuid_respondent/:api_domain', 
      loadComponent: () => import('./app/pages/survey/survey.component').then(m => m.SurveyComponent),
      pathMatch: 'full' 
    },
    { 
      path: 'p/:uuid_survey/:api_domain',
      loadComponent: () => import('./app/pages/survey/survey.component').then(m => m.SurveyComponent),
      pathMatch: 'full' 
    },
    { 
      path: 'p/:uuid_survey/:uuid_respondent/:api_domain', 
      loadComponent: () => import('./app/pages/survey/survey.component').then(m => m.SurveyComponent),
      pathMatch: 'full' 
    },
    { path: 'preview', redirectTo: 'preview/', pathMatch: 'full' },
    { 
      path: 'preview/:r', 
      loadChildren: () => import('./app/pages/preview/preview.module').then(m => m.PreviewModule),
    },
    { 
      path: 'preview/:uuid_respondent/:api_domain', 
      loadChildren: () => import('./app/pages/preview/preview.module').then(m => m.PreviewModule),
    },
    { 
      path: 'not-found', 
      loadChildren: () => import('./app/pages/not-found/not-found.module').then(m => m.NotFoundModule),
      pathMatch: 'full' 
    },
    { 
      path: 'survey-closed', 
      loadChildren: () => import('./app/pages/survey-closed/survey-closed.module').then(m => m.SurveyClosedModule),
      pathMatch: 'full' 
    },
    { 
      path: 'thanks', 
      loadChildren: () => import('./app/pages/thanks/thanks.module').then(m => m.ThanksModule),
    },
    {
      path: 'thanks/external',
      loadChildren: () => import('./app/pages/thank-views/external-link/external-link.module').then(m => m.ExternalLinkModule),
    },
    { 
      path: '**', 
      loadChildren: () => import('./app/pages/not-found/not-found.module').then(m => m.NotFoundModule)
    }
];

const VERSION_STORAGE_KEY = 'qservus-survey:version';
const VERSION_RELOAD_FLAG = 'qservus-survey:version:reloaded';

interface VersionManifest {
  app?: string;
  version?: string;
  environment?: string;
  commit?: string;
  commitLong?: string;
  branch?: string;
  builtAt?: string;
  buildId?: string;
}

function getManifestIdentifier(manifest?: VersionManifest | null): string | null {
  if (!manifest) {
    return null;
  }
  return manifest.buildId || manifest.commitLong || manifest.commit || manifest.version || null;
}

function safeStorageGet(store: Storage | undefined, key: string): string | null {
  if (!store) {
    return null;
  }
  try {
    return store.getItem(key);
  } catch (_) {
    return null;
  }
}

function safeStorageSet(store: Storage | undefined, key: string, value: string): void {
  if (!store) {
    return;
  }
  try {
    store.setItem(key, value);
  } catch (_) {
    // ignore quota / private mode errors
  }
}

function safeStorageRemove(store: Storage | undefined, key: string): void {
  if (!store) {
    return;
  }
  try {
    store.removeItem(key);
  } catch (_) {
    // ignore
  }
}

async function clearServiceWorkerCaches(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if ('caches' in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map(key => caches.delete(key)));
    }
  } catch (error) {
    console.warn('Failed to clear service worker caches:', error);
  }

  try {
    if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    }
  } catch (error) {
    console.warn('Failed to unregister service workers:', error);
  }
}

function shouldEnableServiceWorker(): boolean {
  if (!environment.production || typeof window === 'undefined') {
    return false;
  }
  const isCypressRun = typeof (window as any).Cypress !== 'undefined';
  if (!isCypressRun) {
    return true;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('allow_sw') === '1';
}

async function ensureLatestVersionBeforeBootstrap(): Promise<void> {
  const isCypressRun = typeof window !== 'undefined' && (window as any).Cypress;
  if (isCypressRun) {
    return;
  }
  if (!environment.enableUpdatePreflight || typeof window === 'undefined' || typeof fetch === 'undefined') {
    return;
  }

  const versionUrl = `/meta/version.json?ts=${Date.now()}`;
  try {
    const response = await fetch(versionUrl, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    });
    if (!response.ok) {
      throw new Error(`Version request failed with status ${response.status}`);
    }

    const manifest: VersionManifest = await response.json();
    (window as any).__QSERVUS_VERSION__ = manifest;
    const remoteId = getManifestIdentifier(manifest);
    if (!remoteId) {
      safeStorageRemove(window.sessionStorage, VERSION_RELOAD_FLAG);
      return;
    }

    const storedId = safeStorageGet(window.localStorage, VERSION_STORAGE_KEY);
    const reloadPending = Boolean(safeStorageGet(window.sessionStorage, VERSION_RELOAD_FLAG));

    if (storedId && storedId !== remoteId && !reloadPending) {
      safeStorageSet(window.sessionStorage, VERSION_RELOAD_FLAG, 'true');
      safeStorageSet(window.localStorage, VERSION_STORAGE_KEY, remoteId);
      await clearServiceWorkerCaches();
      window.location.reload();
      return new Promise(() => {});
    }

    safeStorageRemove(window.sessionStorage, VERSION_RELOAD_FLAG);
    safeStorageSet(window.localStorage, VERSION_STORAGE_KEY, remoteId);
  } catch (error) {
    console.warn('Version preflight skipped:', error);
  }
}

if (environment.production) {
  enableProdMode();
  
  // Preload critical components for better perceived performance
  requestIdleOrTimeout(() => {
    import('./app/pages/survey/survey.component')
      .then(() => {
        // console.log('Survey component preloaded');
      })
      .catch(console.warn);
  }, 1500);
}

requestIdleOrTimeoutAsync(async () => {
  initSentry();
  initFirebasePerformance();
}, 1500);

function bootstrapAngularApplication() {
  const bootstrapTrace = startTrace('app_bootstrap');
  return bootstrapApplication(AppComponent, {
    providers: [
        provideRouter(routes),
        importProvidersFrom(BrowserModule, CommonModule, ServiceWorkerModule.register('ngsw-worker.js', { enabled: shouldEnableServiceWorker(), registrationStrategy: 'registerImmediately' }), 
        // AngularFireModule.initializeApp(environment.firebaseConfig, environment.production ? 'qservus-survey-prod' : 'qservus-survey-qa'),
        // Firebase providers - restored to fix injection dependencies
        provideFirebaseApp(() => {
            if (getApps().length > 0) {
                return getApp();
            }
            const app = initializeApp(environment.firebaseConfig);
            // initializeAppCheck(app, {
            //   provider: new ReCaptchaV3Provider(environment.recaptcha.siteKey),
            //   isTokenAutoRefreshEnabled: true
            // })
            return app;
        }), 
        MatMomentDateModule, NativeDateModule, OnlineStatusModule, NgIdleModule.forRoot()),
        TransPipe,
        LocaleDatePipe,
        ThousandsPipe,
        TranslateService,
        UpdateWorkerService,
        {
            provide: APP_INITIALIZER,
            useFactory: setupTranslateFactory,
            deps: [TranslateService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeStorageFactory,
            deps: [StorageManagerService],
            multi: true
        },
        ResponsiveService,
        SensorFormService,
        { provide: DateAdapter, useClass: MomentUtcDateAdapter },
        { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
        { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations(),
        // Material scroll strategies - kept global as needed by multiple components
        {
          provide: MAT_MENU_SCROLL_STRATEGY,
          useFactory: scrollFactory,
          deps: [Overlay],
        },
        {
          provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
          useFactory: scrollFactory,
          deps: [Overlay],
        },
        {
          provide: MAT_TOOLTIP_SCROLL_STRATEGY,
          useFactory: scrollFactory,
          deps: [Overlay],
        },
        {
          provide: MAT_DATEPICKER_SCROLL_STRATEGY,
          useFactory: scrollFactory,
          deps: [Overlay],
        },
        {
          provide: MAT_SELECT_SCROLL_STRATEGY,
          useFactory: scrollFactory,
          deps: [Overlay],
        }
    ]
  }).then(appRef => {
    stopTrace(bootstrapTrace);
    if (environment.enableWebVitalsTracking) {
      requestIdleOrTimeoutAsync(async () => {
        initWebVitalsFirebase(appRef.injector);
      }, 2000);
    }
    requestIdleOrTimeout(() => {
      const storageService = appRef.injector.get(StorageService, null);
      storageService?.scheduleCleanupIdle();
    }, 2000);
    return appRef;
  }).catch(err => console.error(err));
}

ensureLatestVersionBeforeBootstrap()
  .catch(error => {
    console.warn('Skipping update preflight:', error);
  })
  .then(() => bootstrapAngularApplication());
